import apiClient from './client';
import type {
  AuthoredAgendaForCoach,
  AiPromptSelectionsDoc,
  CoachSessionLeaderNote,
} from './coachSessionPrep';

/**
 * Peer Leader Session Prep API client (MED-814 / E4.2).
 *
 * Mirrors `medley-api/types/peerLeaderSessionPrep.ts`. The shape is intentionally
 * lighter than `coachSessionPrep.ts` — no multi-group prep (AC8 N/A), no AI
 * features (E4.6), no prep-sheet PDF. MED-1046 (E4.2a) adds the leader-notes
 * read/write surface (a non-AI self-notes field) that peer leaders inherit
 * from the coach prep flow.
 *
 * The RSVP write goes through the existing MED-905 endpoint
 * `POST /api/sessions/:sessionId/peer-leader-rsvp` — wrapped here so the
 * peer-leader prep page has one import surface.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// MED-1062 (E4.0h): 'missed' — the session was a no-show (peer-led Live Session
// Delivery only) and is pending reschedule; the prep page renders a full
// "Session pending reschedule" state with no Start affordance.
export type PeerLeaderPrepStatus = 'unlocked' | 'locked' | 'live' | 'declined' | 'missed';

export type SessionLeaderRsvpStatus = 'unconfirmed' | 'confirmed' | 'declined';

export interface PeerLeaderReflectionStatus {
  question_count: number;
  answered_count: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  is_locked: boolean;
}

export interface PeerLeaderPrepPayload {
  // MED-1180 — per-cohort LSD gate. Client defense-in-depth: render
  // FeaturesDisabledPage when false (backend also hard-404s the route).
  live_session_delivery_enabled: boolean;
  session: {
    id: string;
    title: string | null;
    session_number: number | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    duration_minutes: number | null;
    timezone: string | null;
    cohort_id: string;
    cohort_name: string | null;
    program_name: string | null;
    organization_name: string | null;
    group_id: string | null;
    group_name: string | null;
    status: string;
    /** MED-816 — drives the AI disclosure gate on the Start Session button. */
    ai_features_enabled: boolean;
    /** MED-909 AC4 — current AI Listening state (read-only to the peer leader). */
    ai_listening_enabled: boolean;
    /** MED-909 AC4 — true once within 15 min of start; listening value is frozen. */
    ai_listening_locked: boolean;
  };
  prep_status: PeerLeaderPrepStatus;
  prep_locked_reason: string | null;
  rsvp_status: SessionLeaderRsvpStatus;
  agenda: AuthoredAgendaForCoach | null;
  /**
   * MED-1046 (E4.2a) — the peer leader's own per-agenda-section Leader Notes,
   * keyed by `${sectionIdx}:0`. Reloaded on every prep open (AC2). Always
   * scope='all_groups' (single group, AC4).
   */
  leader_notes: CoachSessionLeaderNote[];
  reflection_status: PeerLeaderReflectionStatus | null;
  prep_state: {
    marked_completed_at: string | null;
    step2_reviewed_at: string | null;
    step3_visited_at: string | null;
    /** MED-909 AC1-AC2 — In Use prompt selections; drives the banner + per-step badges. */
    ai_prompt_selections: AiPromptSelectionsDoc;
  };
  is_first_time_leader: boolean;
  cm_name: string | null;
}

export interface PeerLeaderRsvpResponse {
  session_id: string;
  session_leader_rsvp_status: SessionLeaderRsvpStatus;
  session_leader_rsvp_responded_at: string | null;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const peerLeaderSessionPrepApi = {
  /** GET /api/participant/sessions/:sessionId/peer-leader-prep */
  getPrep: async (sessionId: string): Promise<PeerLeaderPrepPayload> => {
    return apiClient.get<PeerLeaderPrepPayload>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep`,
    );
  },

  /**
   * POST presence heartbeat (MED-1126). Keeps the peer leader 'present' in the
   * participant waiting room while they prep — peer leaders never enter the
   * waiting room (prep → Start → active), so the prep page pings this on the
   * waiting room's ~4s cadence. Fire-and-forget: 204 No Content.
   */
  heartbeat: async (sessionId: string): Promise<void> => {
    await apiClient.post<void>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/heartbeat`,
    );
  },

  /** POST mark the prep sequence completed (AC13). */
  markCompleted: async (sessionId: string): Promise<PeerLeaderPrepPayload> => {
    return apiClient.post<PeerLeaderPrepPayload>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/mark-completed`,
    );
  },

  /** POST toggle the Step 2 reviewed marker (AC11). */
  setStep2Reviewed: async (
    sessionId: string,
    reviewed: boolean,
  ): Promise<PeerLeaderPrepPayload> => {
    return apiClient.post<PeerLeaderPrepPayload>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/step/2/reviewed`,
      { reviewed },
    );
  },

  /** POST mark Step 3 visited; idempotent (AC11). */
  setStep3Visited: async (sessionId: string): Promise<PeerLeaderPrepPayload> => {
    return apiClient.post<PeerLeaderPrepPayload>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/step/3/visited`,
    );
  },

  /**
   * PUT one per-agenda-section Leader Note (MED-1046 AC1-AC4). The peer leader
   * facilitates one group, so the request carries no scope/group_id — the API
   * fixes scope='all_groups'. An empty `content_html` soft-deletes the note and
   * resolves to `null`.
   */
  saveLeaderNote: async (
    sessionId: string,
    body: { block_key: string; content_html: string },
  ): Promise<CoachSessionLeaderNote | null> => {
    return apiClient.put<CoachSessionLeaderNote | null>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/leader-notes`,
      body,
    );
  },

  /**
   * PATCH the AI prompt In Use selections document (MED-909 AC1-AC2).
   * Replaces the stored document atomically and returns the persisted result.
   */
  patchAiSelections: async (
    sessionId: string,
    selections: AiPromptSelectionsDoc,
  ): Promise<{ ai_prompt_selections: AiPromptSelectionsDoc }> => {
    return apiClient.patch<{ ai_prompt_selections: AiPromptSelectionsDoc }>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/ai-selections`,
      { selections },
    );
  },

  /**
   * PATCH AI Listening on/off for this peer-led session (MED-909 QA).
   *
   * Diverges from MED-909 AC4 (CM-only control) per QA: the designated peer
   * session leader toggles AI Listening the same way a coach does on coach prep.
   * Returns the persisted state + lock flag.
   */
  setAiListening: async (
    sessionId: string,
    enabled: boolean,
  ): Promise<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }> => {
    return apiClient.patch<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }>(
      `/api/participant/sessions/${sessionId}/peer-leader-prep/ai-listening`,
      { enabled },
    );
  },

  /**
   * Wrap the existing MED-905 RSVP endpoint so the prep page has a single
   * client to import. Calls `POST /api/sessions/:sessionId/peer-leader-rsvp`.
   */
  setRsvp: async (
    sessionId: string,
    status: 'confirmed' | 'declined',
  ): Promise<PeerLeaderRsvpResponse> => {
    return apiClient.post<PeerLeaderRsvpResponse>(
      `/api/sessions/${sessionId}/peer-leader-rsvp`,
      { status },
    );
  },
};

export default peerLeaderSessionPrepApi;
